import type { Panel } from '../panel.class';
import { Vertex3 } from '../../../spatial-classes';
import { distributeApplianceGroupsHorizontallyCfn } from './distribute-appliance-groups-horizontally.cfn';
import { getPanelOverlapCfn } from './panel-space.cfn';
import { resetFineTuneCfn } from './fine-tune-group-position.cfn';

export interface PlaceGroupAtParams {
	groupId: string;
	/** Target `center.x`; only its ORDER relative to the other columns decides the slot */
	x: number;
}

/**
 * Drops one column at an absolute x and re-spaces the panel. The x is clamped
 * to the usable run (connected-island overlap excluded) rather than ignored,
 * which is what `PanelItemGroup.move` would do with an out-of-range request.
 */
export const placeGroupAtCfn = (panel: Panel, { groupId, x }: PlaceGroupAtParams): void => {
	const group = panel.panelItemGroups.children.find((g) => g.id === groupId);
	if (!group) throw new Error(`placeGroupAt: group '${groupId}' is not on panel '${panel.id}'.`);
	if (group.positionLocked) throw new Error(`placeGroupAt: group '${groupId}' is position-locked.`);

	const overlap = getPanelOverlapCfn(panel);
	const halfWidth = panel.size.width / 2;
	const clamped = Math.min(halfWidth - overlap.right, Math.max(-halfWidth + overlap.left, x));

	// a reorder re-spaces the whole run: every hand-placed position on the panel is forgotten
	resetFineTuneCfn(panel);
	group.center = new Vertex3({ dataIn: { x: clamped }});
	distributeApplianceGroupsHorizontallyCfn(panel);
};

export interface SwapGroupPositionsParams {
	groupIdA: string;
	groupIdB: string;
}

/**
 * Positional swap on one panel: the two columns trade x values and the panel is
 * re-spaced, so they trade slots. Both columns, with every companion inside
 * them, stay in the kitchen — this is NOT `PanelItem.swapItem`, which puts a
 * different SKU in the same spot.
 */
export const swapApplianceGroupPositionsCfn = (panel: Panel, { groupIdA, groupIdB }: SwapGroupPositionsParams): void => {
	const a = panel.panelItemGroups.children.find((g) => g.id === groupIdA);
	const b = panel.panelItemGroups.children.find((g) => g.id === groupIdB);
	if (!a || !b) throw new Error(`swapApplianceGroupPositions: both groups must be on panel '${panel.id}'.`);
	if (a === b) return;
	if (a.positionLocked || b.positionLocked) throw new Error('swapApplianceGroupPositions: a position-locked group cannot trade places.');

	const ax = a.center.x;
	const bx = b.center.x;
	resetFineTuneCfn(panel);
	a.center = new Vertex3({ dataIn: { x: bx }});
	b.center = new Vertex3({ dataIn: { x: ax }});
	distributeApplianceGroupsHorizontallyCfn(panel);
};
